import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/AxiosInstance";

const MAX_KEYWORDS = 10;

export default function ScoutKeywordsSettings() {
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchKeywords();
  }, []);

  const fetchKeywords = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("discover/scout/keywords/");
      setKeywords(res.data);
    } catch {
      setError("Failed to load keywords or no keywords added.");
    } finally {
      setLoading(false);
    }
  };

    const MIN_KEYWORD_LENGTH = 3;
    const MAX_KEYWORD_LENGTH = 100;

    const handleAddKeyword = async () => {
    const trimmed = newKeyword.trim();

    if (!trimmed) return;

    // Length checks
    if (trimmed.length < MIN_KEYWORD_LENGTH) {
        setError(`Keyword must be at least ${MIN_KEYWORD_LENGTH} characters.`);
        return;
    }
    if (trimmed.length > MAX_KEYWORD_LENGTH) {
        setError(`Keyword must be no more than ${MAX_KEYWORD_LENGTH} characters.`);
        return;
    }

    // Regex: allow only alphanumerics, spaces, hyphens
    const validKeyword = /^[a-zA-Z0-9\- ]+$/;
    if (!validKeyword.test(trimmed)) {
        setError("Keyword contains invalid characters. Only letters, numbers, hyphens, and spaces are allowed.");
        return;
    }

    // Prevent duplicates (case-insensitive)
    if (keywords.some(k => k.keyword.toLowerCase() === trimmed.toLowerCase())) {
        setError("Keyword already exists.");
        return;
    }

    if (keywords.length >= MAX_KEYWORDS) {
        setError(`Keyword limit reached (max ${MAX_KEYWORDS}).`);
        return;
    }

    // Proceed
    setLoading(true);
    setError(null);
    try {
        await axiosInstance.post("discover/scout/keywords/", { keyword: trimmed });
        setNewKeyword("");
        fetchKeywords();
    } catch {
        setError("Failed to add keyword.");
    } finally {
        setLoading(false);
    }
    };

  const handleDeleteKeyword = async (id) => {
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`discover/scout/keywords/${id}/delete/`);
      fetchKeywords();
    } catch {
      setError("Failed to delete keyword.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-4">
      <h2 className="text-2xl font-bold mb-6">Scout Monitor Keywords</h2>

      {error && <p className="text-red-500 mb-2">{error}</p>}

      <div className="flex mb-4">
        <input
          type="text"
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          placeholder="Enter new keyword"
          className="flex-grow border rounded-l px-3 py-2 text-gray-900 bg-white placeholder-gray-400"
          maxLength={100}
          aria-label="New keyword input"
          disabled={loading || keywords.length >= MAX_KEYWORDS}
        />
        <button
          onClick={handleAddKeyword}
          disabled={loading || !newKeyword.trim() || keywords.length >= MAX_KEYWORDS}
          className={`px-4 rounded-r text-white ${
            keywords.length >= MAX_KEYWORDS
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-purple-600 hover:bg-purple-700"
          }`}
          aria-disabled={keywords.length >= MAX_KEYWORDS}
          aria-label="Add keyword"
        >
          Add
        </button>
      </div>

      {loading && <p>Loading...</p>}

      {!loading && keywords.length === 0 && <p>No keywords added yet.</p>}

      <ul>
        {keywords.map((kw) => (
          <li
            key={kw.id}
            className="flex justify-between items-center py-1 border-b"
          >
            <span>{kw.keyword}</span>
            <button
              onClick={() => handleDeleteKeyword(kw.id)}
              disabled={loading}
              className="text-red-600 hover:text-red-800 font-semibold"
              aria-label={`Delete keyword ${kw.keyword}`}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
